import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Shield, ShieldOff } from "lucide-react";
import { getConsent, setConsent, clearConsent, onConsentChange } from "@/lib/consent";

/**
 * Privacy settings toggle. Users can opt in/out of analytics anytime.
 * Disabling tracking does NOT affect core booking, search or payment functionality.
 */
export function PrivacyToggle() {
  const [analytics, setAnalytics] = useState(false);
  const [decided, setDecided] = useState(false);

  useEffect(() => {
    const sync = () => {
      const c = getConsent();
      setAnalytics(c?.analytics ?? false);
      setDecided(c != null);
    };
    sync();
    return onConsentChange(sync);
  }, []);

  const toggle = (next: boolean) => {
    setAnalytics(next);
    setConsent({ analytics: next, marketing: next });
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-start gap-3">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${analytics ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
          {analytics ? <Shield className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
        </span>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold">Analytics &amp; product improvement</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Helps us understand where users get stuck so we can fix it. When off, we record nothing beyond what's needed to deliver the service.
              </p>
            </div>
            <Switch checked={analytics} onCheckedChange={toggle} aria-label="Toggle analytics" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {decided
              ? analytics ? "Tracking is on. You can switch off anytime." : "Tracking is off. Core booking, search and payments are unaffected."
              : "You haven't made a choice yet. Choose Essential only or Accept all from the cookie banner, or toggle here."}
          </p>
          {decided && (
            <Button variant="ghost" size="sm" className="mt-2 px-0 text-xs" onClick={() => clearConsent()}>
              Reset choice (show banner again)
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
